import api from './api';
import { ApiResponse } from '../types';

/** What a category may be saved with; `glAccountCode` null means "use the suggestion". */
export interface CategoryInput { name?: string; description?: string; glAccountCode?: string | null }

const data = <T>(response: { data: ApiResponse<T> }): T => {
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data as T;
};

/** Calls for a two-level category tree (expense and income categories share the API). */
export const categoryTreeService = <C, S>(base: string) => ({
  getCategories: async (params?: { search?: string; schoolId?: string }): Promise<C[]> => {
    const q = new URLSearchParams();
    if (params?.search) q.append('search', params.search);
    if (params?.schoolId) q.append('schoolId', params.schoolId);
    return data<{ categories: C[] }>(await api.get(`${base}?${q.toString()}`)).categories;
  },
  createCategory: async (input: CategoryInput & { name: string }): Promise<C> => data<C>(await api.post(base, input)),
  updateCategory: async (id: string, input: CategoryInput): Promise<C> => data<C>(await api.put(`${base}/${id}`, input)),
  deleteCategory: async (id: string): Promise<void> => { data(await api.delete(`${base}/${id}`)); },
  getSubCategories: async (categoryId: string): Promise<S[]> =>
    data<{ subCategories: S[] }>(await api.get(`${base}/${categoryId}/subcategories`)).subCategories,
  getAllSubCategories: async (): Promise<S[]> => data<{ subCategories: S[] }>(await api.get(`${base}/subcategories/all`)).subCategories,
  createSubCategory: async (categoryId: string, input: { name: string; description?: string }): Promise<S> =>
    data<S>(await api.post(`${base}/${categoryId}/subcategories`, input)),
  updateSubCategory: async (categoryId: string, id: string, input: { name?: string; description?: string }): Promise<S> =>
    data<S>(await api.put(`${base}/${categoryId}/subcategories/${id}`, input)),
  deleteSubCategory: async (categoryId: string, id: string): Promise<void> => { data(await api.delete(`${base}/${categoryId}/subcategories/${id}`)); },
});

export type CategoryTreeService<C, S> = ReturnType<typeof categoryTreeService<C, S>>;
